import React, {
  Dispatch,
  PropsWithChildren,
  createContext,
  useContext,
  useReducer,
  useEffect
} from 'react';
import { getChatConversations } from 'services/chatService';
import { Conversation } from 'data/chat'; 
import { ACTIONTYPE, SET_CHAT_STATE, chatReducer } from 'reducers/ChatReducer';
import { socket } from 'config/socket'; 

// 🚀 1. CAMBIAMOS LOS FILTROS GLOBALES A LOS ESTADOS DEL CRM
export type ConversationFilterType = 'all' | 'pendientes' | 'resueltas';

interface ChatProviderInterface {}

export interface ChatState {
  conversations: Conversation[];
  currentConversation: null | Conversation;
  filterBy: ConversationFilterType;
  showConversationDetails: boolean;
  showUserListOffcanvas: boolean;
}

interface ChatContextInterface extends ChatState {
  chatDispatch: Dispatch<ACTIONTYPE>;
  setShowConversationDetails: (value: boolean) => void;
  setShowUserListOffcanvas: (value: boolean) => void;
}

export const ChatContext = createContext({} as ChatContextInterface);

const ChatProvider = ({ children }: PropsWithChildren<ChatProviderInterface>) => {
  const initState: ChatState = {
    conversations: [],
    currentConversation: null,
    // 🚀 2. LA VISTA PRINCIPAL POR DEFECTO AHORA ES PENDIENTES
    filterBy: 'pendientes', 
    showUserListOffcanvas: false,
    showConversationDetails: false
  };

  const [chatState, chatDispatch] = useReducer(chatReducer, initState);

  // EFECTO PARA CARGAR LOS CHATS DE TU BASE DE DATOS
  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getChatConversations();
        
        // Opcional pero recomendado: Al iniciar, queremos seleccionar 
        // por defecto la primera conversación que esté PENDIENTE.
        const firstPending = data.find((c: Conversation) => c.estado === 'Pendiente') || null;

        chatDispatch({
          type: SET_CHAT_STATE,
          payload: { 
            conversations: data,
            currentConversation: firstPending || (data.length > 0 ? data[0] : null)
          }
        });
      } catch (error) {
        console.error("Error al conectar con el servidor de Node:", error);
      }
    };

    loadData();
  }, []);

  const setShowConversationDetails = (value: boolean) => {
    chatDispatch({
      type: SET_CHAT_STATE,
      payload: { showConversationDetails: value }
    });
  };

  const setShowUserListOffcanvas = (value: boolean) => {
    chatDispatch({
      type: SET_CHAT_STATE,
      payload: { showUserListOffcanvas: value }
    });
  };

  useEffect(() => {
    // 1. Conectar el socket cuando se monta el chat
    socket.connect();

    // 2. Escuchar mensajes nuevos
    socket.on('new_inbound_message', (payload) => {
      console.log("📩 Mensaje recibido por socket:", payload);
      chatDispatch({
        type: 'NEW_INBOUND_MESSAGE',
        payload: payload
      });
    });

    // 3. Limpiar al cerrar el chat
    return () => {
      socket.off('new_inbound_message');
      socket.disconnect();
    };
  }, []);

  return (
    <ChatContext.Provider
      value={{
        ...chatState,
        chatDispatch,
        setShowConversationDetails,
        setShowUserListOffcanvas
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => useContext(ChatContext);

export default ChatProvider;